import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { AddBenefitDialog } from '../program-create-benefits/AddBenefitDialog';
import type { ProgramEditStep } from '../program-edit/ProgramEditShell';
import type { ProgramAction } from '../program-actions-menu';
import { useProgramExportMenu } from '../program-export/use-program-export-menu';
import { useDeleteProgram, usePatchProgram } from '../program-edit/program-mutations';
import { programByIdQueryOptions } from '../program-edit/program-by-id-query';
import { isProgramReadOnly, mapProgramStatusKey } from '../program-edit/program-status';
import { ProgramEditBenefitsPage as ProgramEditBenefitsPageComponent } from './ProgramEditBenefitsPage.component';
import {
  ADDITIONAL_PAYMENT_OPTIONS,
  BENEFIT_OPTIONS,
} from '../program-create-benefits/ProgramBenefitsPage.mock';

type AddDialog = 'benefit' | 'additional-payment' | null;

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((value) => set.has(value));
}

export default function ProgramEditBenefitsPage() {
  const navigate = useNavigate();
  const { programId } = useParams({
    from: '/programas/$programId/editar/beneficios',
  });
  const { copy: isCopy = false } = useSearch({
    from: '/programas/$programId/editar/beneficios',
  });

  const { data: program } = useSuspenseQuery(programByIdQueryOptions(programId));
  const deleteProgram = useDeleteProgram(programId);
  const patchProgram = usePatchProgram(programId);
  const { exportTypes, handleExportAction } = useProgramExportMenu(programId);

  // EU-570 — status REAL do programa (antes era hardcoded 'ativa' e o badge
  // mentia para Encerrado/Cancelado); Encerrado/Cancelado = read-only.
  const status = isCopy ? ('rascunho' as const) : mapProgramStatusKey(program.statusKey);
  const isReadOnly = !isCopy && isProgramReadOnly(status);

  // Hydrate from the joined detail payload (`program.benefits` /
  // `program.additionalPayments` are arrays of `{ id, key, name }`). The
  // toggle UI tracks names, so the seed is each row's `.name`.
  const initialBenefits = useMemo(() => program.benefits.map((b) => b.name), [program.benefits]);
  const initialAdditionalPayments = useMemo(
    () => program.additionalPayments.map((p) => p.name),
    [program.additionalPayments]
  );

  const [benefitOptions, setBenefitOptions] = useState<string[]>(() => [
    ...new Set([...BENEFIT_OPTIONS, ...initialBenefits]),
  ]);
  const [additionalPaymentOptions, setAdditionalPaymentOptions] = useState<string[]>(() => [
    ...new Set([...ADDITIONAL_PAYMENT_OPTIONS, ...initialAdditionalPayments]),
  ]);
  const [selectedBenefits, setSelectedBenefits] = useState<string[]>(() => [...initialBenefits]);
  const [selectedAdditionalPayments, setSelectedAdditionalPayments] = useState<string[]>(() => [
    ...initialAdditionalPayments,
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStepsOpen, setIsStepsOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<AddDialog>(null);

  const isDirty = useMemo(
    () =>
      !sameSet(selectedBenefits, initialBenefits) ||
      !sameSet(selectedAdditionalPayments, initialAdditionalPayments),
    [selectedBenefits, selectedAdditionalPayments, initialBenefits, initialAdditionalPayments]
  );

  function toggle(list: string[], value: string) {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  function addIfMissing(list: string[], value: string) {
    return list.includes(value) ? list : [...list, value];
  }

  const copySearch = isCopy ? { copy: true } : {};

  function handleStepChange(step: ProgramEditStep) {
    setIsStepsOpen(false);
    if (step === 'info') {
      void navigate({
        to: '/programas/$programId/editar',
        params: { programId },
        search: copySearch,
      });
      return;
    }
    if (step === 'faqs') {
      void navigate({
        to: '/programas/$programId/editar/perguntas-frequentes',
        params: { programId },
        search: copySearch,
      });
    }
  }

  function handleBack() {
    void navigate({
      to: '/programas/$programId/editar',
      params: { programId },
      search: copySearch,
    });
  }

  async function persistBenefits() {
    // Replace-set semantics on the BFF: the server resolves names to ids and
    // silently drops unmatched (custom) names. UX caveat is documented above.
    await patchProgram.mutateAsync({
      benefitNames: selectedBenefits,
      additionalPaymentNames: selectedAdditionalPayments,
    });
  }

  async function handleSaveChanges() {
    if (isReadOnly) return; // EU-570 — guard defensivo (botão não renderiza).
    await persistBenefits();
    toast('Benefícios salvos', {
      action: { label: 'Fechar', onClick: () => toast.dismiss() },
    });
  }

  async function handleDiscardCopy() {
    await deleteProgram.mutateAsync();
    void navigate({ to: '/programas' });
  }

  function handleHeaderAction(action: ProgramAction) {
    // EU-654 - secao Extrair bases do header (toast de 3 estados).
    if (handleExportAction(action)) {
      return;
    }
    if (action === 'discard') {
      void handleDiscardCopy();
    }
    // Other actions (publish/end/cancel/duplicate) — wired in EU-149-followup.
  }

  async function handleSubmit() {
    if (isReadOnly) {
      // EU-570 — "Avançar" fica disponível apenas para navegação.
      await navigate({
        to: '/programas/$programId/editar/perguntas-frequentes',
        params: { programId },
        search: copySearch,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await persistBenefits();
      await navigate({
        to: '/programas/$programId/editar/perguntas-frequentes',
        params: { programId },
        search: copySearch,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleConfirmAddBenefit(value: string) {
    setBenefitOptions((prev) => addIfMissing(prev, value));
    setSelectedBenefits((prev) => addIfMissing(prev, value));
    setActiveDialog(null);
    toast('Benefício adicionado', {
      action: { label: 'Fechar', onClick: () => toast.dismiss() },
    });
  }

  function handleConfirmAddAdditionalPayment(value: string) {
    setAdditionalPaymentOptions((prev) => addIfMissing(prev, value));
    setSelectedAdditionalPayments((prev) => addIfMissing(prev, value));
    setActiveDialog(null);
    toast('Pagamento adicional adicionado', {
      action: { label: 'Fechar', onClick: () => toast.dismiss() },
    });
  }

  return (
    <>
      <ProgramEditBenefitsPageComponent
        status={status}
        readOnly={isReadOnly}
        benefitOptions={benefitOptions}
        selectedBenefits={selectedBenefits}
        onToggleBenefit={(value) => setSelectedBenefits((prev) => toggle(prev, value))}
        onAddBenefit={() => setActiveDialog('benefit')}
        additionalPaymentOptions={additionalPaymentOptions}
        selectedAdditionalPayments={selectedAdditionalPayments}
        onToggleAdditionalPayment={(value) =>
          setSelectedAdditionalPayments((prev) => toggle(prev, value))
        }
        onAddAdditionalPayment={() => setActiveDialog('additional-payment')}
        progress={60}
        currentStep={2}
        totalSteps={3}
        isStepsOpen={isStepsOpen}
        onStepsOpenChange={setIsStepsOpen}
        onStepChange={handleStepChange}
        isSubmitting={isSubmitting || deleteProgram.isPending || patchProgram.isPending}
        isDirty={isDirty}
        isCopy={isCopy}
        onDiscardCopy={() => void handleDiscardCopy()}
        onBack={handleBack}
        onSaveChanges={() => void handleSaveChanges()}
        onSubmit={() => void handleSubmit()}
        onHeaderAction={handleHeaderAction}
        exportTypes={exportTypes}
      />
      <AddBenefitDialog
        open={activeDialog === 'benefit'}
        onOpenChange={(open) => setActiveDialog(open ? 'benefit' : null)}
        title="Adicionar benefício"
        placeholder="Digite o nome do benefício"
        onAdd={handleConfirmAddBenefit}
      />
      <AddBenefitDialog
        open={activeDialog === 'additional-payment'}
        onOpenChange={(open) => setActiveDialog(open ? 'additional-payment' : null)}
        title="Adicionar pagamento adicional"
        placeholder="Digite o nome do pagamento adicional"
        onAdd={handleConfirmAddAdditionalPayment}
      />
    </>
  );
}
